const KEY = "vedaya_demo_store_v1";

function nowIso() {
  return new Date().toISOString();
}

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function save(state) {
  localStorage.setItem(KEY, JSON.stringify(state));
  window.dispatchEvent(new Event("vedaya-demo-store"));
}

function seedIfEmpty() {
  const existing = load();
  if (existing) return existing;

  const state = {
    ahaComplete: false,
    tickets: [],
    approvals: [],
    integrations: {
      okta: false,
    },
  };

  save(state);
  return state;
}

// Ensure existing stores get the integrations field
function ensureIntegrations(s) {
  if (!s.integrations) {
    s.integrations = { okta: false };
  }
  return s;
}

export function getState() {
  const s = seedIfEmpty();
  ensureIntegrations(s);
  return s;
}

export function isIntegrationConnected(integrationId) {
  const s = seedIfEmpty();
  ensureIntegrations(s);
  return !!s.integrations[integrationId];
}

export function connectIntegration(integrationId) {
  const s = seedIfEmpty();
  ensureIntegrations(s);
  s.integrations[integrationId] = true;
  save(s);
}

export function disconnectIntegration(integrationId) {
  const s = seedIfEmpty();
  ensureIntegrations(s);
  s.integrations[integrationId] = false;
  save(s);
}

export function setAhaComplete(value) {
  const s = seedIfEmpty();
  s.ahaComplete = !!value;
  save(s);
}

export function isAhaComplete() {
  return !!seedIfEmpty().ahaComplete;
}

export function listTickets() {
  return seedIfEmpty().tickets;
}

export function getTicket(id) {
  return seedIfEmpty().tickets.find(t => t.id === id) || null;
}

export function upsertTicket(ticket) {
  const s = seedIfEmpty();
  const idx = s.tickets.findIndex(t => t.id === ticket.id);
  if (idx >= 0) s.tickets[idx] = ticket;
  else s.tickets.unshift(ticket);
  save(s);
}

export function createTicket({ subject, description, requester }) {
  const s = seedIfEmpty();
  const n = 2000 + Math.floor(Math.random() * 7000);
  const id = `TKT-${n}`;
  const needsApproval = /access|salesforce|admin|aws|prod/i.test(subject || description || "");
  const now = nowIso();
  const req = requester || { name: "Requester", email: "requester@company.com", avatar: "R" };

  const ticket = {
    id,
    subject: subject || "New request",
    description: description || "",
    requester: {
      ...req,
      department: req.department || "Unknown",
      manager: req.manager || "Manager",
      startDate: req.startDate || new Date().toISOString().split('T')[0],
    },
    channel: { type: "portal", name: "Self-Service Portal" },
    status: needsApproval ? "pending_approval" : "open",
    priority: "medium",
    category: needsApproval ? "Access Request" : "General",
    assignedTo: { type: "team", name: needsApproval ? "Identity & Access" : "IT Support" },
    createdAt: now,
    lastActivity: "Just now",
    messages: [
      { id: `msg-${Date.now()}-1`, author: { name: req.name, avatar: req.avatar, type: "requester" }, content: description || subject || "", timestamp: now, isInternal: false },
      { id: `msg-${Date.now()}-2`, author: { name: "Vedaya AI", avatar: "V", type: "ai" }, content: needsApproval ? "Got it. I've identified this requires manager approval. I'll send the request to your manager." : "Got it! I'm working on this now.", timestamp: now, isInternal: false },
    ],
    suggestedActions: [
      {
        id: `act-${Date.now()}`,
        name: /password|mfa/i.test(subject || description || "") ? "Send password reset link" : (needsApproval ? "Grant requested access" : "Execute recommended workflow"),
        confidence: 92,
        workflow: needsApproval ? "Standard App Access" : "Demo Workflow",
        systems: ["Okta"],
        requiresApproval: needsApproval,
        approver: "Mike Torres",
      },
    ],
    relatedTickets: [],
    auditLog: [
      { action: "Ticket created via Portal", timestamp: now, actor: "System" },
      { action: needsApproval ? "AI classified as access request" : "AI classified as general request", timestamp: now, actor: "Vedaya AI" },
    ],
  };

  s.tickets.unshift(ticket);
  save(s);
  return ticket;
}

export function listApprovals() {
  return seedIfEmpty().approvals;
}

export function getApproval(id) {
  return seedIfEmpty().approvals.find(a => a.id === id) || null;
}

export function createApprovalFromTicket({ ticketId, title, description, actions, risk }) {
  const s = seedIfEmpty();
  const id = `APR-${100 + Math.floor(Math.random() * 900)}`;
  const approval = {
    id,
    title,
    description,
    ticketId,
    actions: actions || [],
    risk: risk || "low",
    status: "pending",
    requestedAt: nowIso(),
    requester: { name: "Sarah Chen", email: "sarah@company.com", avatar: "SC" },
  };
  s.approvals.unshift(approval);
  save(s);
  return approval;
}

export function resolveApproval(id, status) {
  const s = seedIfEmpty();
  const a = s.approvals.find(x => x.id === id);
  if (!a) return null;
  a.status = status;

  if (status === "approved" && a.ticketId) {
    const t = s.tickets.find(x => x.id === a.ticketId);
    if (t) {
      t.status = "resolved";
      t.lastActivity = "Just now";
      t.messages.push({
        id: `msg-${Date.now()}`,
        author: { name: "Vedaya AI", avatar: "V", type: "ai" },
        content: "Approval received. Action executed successfully. Ticket resolved.",
        timestamp: nowIso(),
        isInternal: false,
      });
    }
    setAhaComplete(true);
  }

  save(s);
  return a;
}

// Approve and execute: mark approval approved, resolve ticket, trigger aha
export function approveAndExecute(approvalId) {
  const s = seedIfEmpty();
  const approval = s.approvals.find(a => a.id === approvalId);
  if (!approval) return null;

  const now = nowIso();
  approval.status = "approved";
  approval.decidedAt = now;

  // Find and update linked ticket
  if (approval.ticketId) {
    const ticket = s.tickets.find(t => t.id === approval.ticketId);
    if (ticket) {
      // Add approval message
      ticket.messages.push({
        id: `msg-${Date.now()}-1`,
        author: { name: "Vedaya AI", avatar: "V", type: "ai" },
        content: "Manager approved. Executing actions now...",
        timestamp: now,
        isInternal: false,
      });

      // Add execution message
      const actionNames = (approval.actions || []).map(a => a.name || a).join(", ") || "requested action";
      ticket.messages.push({
        id: `msg-${Date.now()}-2`,
        author: { name: "Vedaya AI", avatar: "V", type: "ai" },
        content: `Done! Executed: ${actionNames}. Access has been granted.`,
        timestamp: now,
        isInternal: false,
      });

      ticket.status = "resolved";
      ticket.lastActivity = "Just now";
      ticket.auditLog = ticket.auditLog || [];
      ticket.auditLog.push({
        action: "Approval received from manager",
        timestamp: now,
        actor: "Mike Torres",
      });
      ticket.auditLog.push({
        action: "Actions executed automatically",
        timestamp: now,
        actor: "Vedaya AI",
      });
    }
  }

  // Trigger aha completion
  s.ahaComplete = true;
  save(s);
  return approval;
}

// Reject approval: mark rejected, update ticket status
export function rejectApproval(approvalId, reason) {
  const s = seedIfEmpty();
  const approval = s.approvals.find(a => a.id === approvalId);
  if (!approval) return null;

  const now = nowIso();
  approval.status = "rejected";
  approval.decidedAt = now;
  approval.rejectionReason = reason || "Request denied";

  // Find and update linked ticket
  if (approval.ticketId) {
    const ticket = s.tickets.find(t => t.id === approval.ticketId);
    if (ticket) {
      ticket.messages.push({
        id: `msg-${Date.now()}`,
        author: { name: "Vedaya AI", avatar: "V", type: "ai" },
        content: `Request was denied by manager. Reason: ${reason || "Not specified"}`,
        timestamp: now,
        isInternal: false,
      });

      ticket.status = "closed";
      ticket.lastActivity = "Just now";
      ticket.auditLog = ticket.auditLog || [];
      ticket.auditLog.push({
        action: `Approval rejected: ${reason || "No reason provided"}`,
        timestamp: now,
        actor: "Mike Torres",
      });
    }
  }

  save(s);
  return approval;
}

export function subscribe(cb) {
  const handler = () => cb(getState());
  window.addEventListener("storage", handler);
  window.addEventListener("vedaya-demo-store", handler);
  return () => {
    window.removeEventListener("storage", handler);
    window.removeEventListener("vedaya-demo-store", handler);
  };
}

export function resetDemoStore() {
  localStorage.removeItem(KEY);
  seedIfEmpty();
  window.dispatchEvent(new Event("vedaya-demo-store"));
}
